import React, { useState, useEffect } from 'react';
import { api, useServer } from '../contexts/ServerContext';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { Tag, Plus, X, Crown, Trash2 } from 'lucide-react';

function PlayerTags({ steamId, onTagsUpdate }) {
  const { server } = useServer();
  const { user } = useAuth();
  const [playerTags, setPlayerTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRank, setUserRank] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState('');
  const [tagToRemove, setTagToRemove] = useState(null);

  useEffect(() => {
    fetchPlayerTags();
    fetchAllTags();
    fetchUserRank();
  }, [steamId, server]);

  const fetchPlayerTags = async () => {
    try {
      // console.log'Получаем теги игрока:', steamId);
      const response = await api.get(`/api/tags/player/${steamId}?server=${server}`);
      // console.log'Теги игрока получены:', response.data);
      setPlayerTags(response.data.data || response.data || []);
    } catch (error) {
      console.error('Ошибка при получении тегов игрока:', error);
      console.error('Детали ошибки:', error.response?.data);
    }
  };

  const fetchAllTags = async () => {
    try {
      // console.log'Получаем все теги...');
      const response = await api.get(`/api/tags?server=${server}`);
      // console.log'Все теги получены:', response.data);
      setAllTags(response.data.data || response.data || []);
    } catch (error) {
      console.error('Ошибка при получении всех тегов:', error);
      console.error('Детали ошибки:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserRank = async () => {
    try {
      // console.log'Получаем ранг пользователя...');
      const response = await api.get(`/admin/rank?server=${server}`);
      // console.log'Ранг пользователя:', response.data.rank);
      setUserRank(response.data.rank);
    } catch (error) {
      console.error('Ошибка при получении ранга пользователя:', error);
      // Если не удалось получить ранг, используем ранг из контекста
      if (user?.rank) {
        // console.log'Используем ранг из контекста:', user.rank);
        setUserRank(user.rank);
      }
    }
  };

  const handleAssignTag = async () => {
    if (!selectedTagId) {
      toast.error('Выберите тег для назначения');
      return;
    }

    try {
      await api.post(`/api/tags/assign?server=${server}`, {
        steamId,
        tagId: selectedTagId
      });
      toast.success('Тег назначен игроку');
      setShowAssignModal(false);
      setSelectedTagId('');
      fetchPlayerTags();
      if (onTagsUpdate) onTagsUpdate();
    } catch (error) {
      console.error('Ошибка при назначении тега:', error);
      toast.error(error.response?.data?.error || 'Ошибка при назначении тега');
    }
  };

  const handleRemoveTag = async (tagId) => {
    const tag = playerTags.find(t => t.id === tagId);
    if (tag) {
      setTagToRemove(tag);
      setShowRemoveModal(true);
    }
  };

  const confirmRemoveTag = async () => {
    if (!tagToRemove) return;

    try {
      await api.delete(`/api/tags/assign/${steamId}/${tagToRemove.id}?server=${server}`);
      toast.success('Тег убран у игрока');
      setShowRemoveModal(false);
      setTagToRemove(null);
      fetchPlayerTags();
      if (onTagsUpdate) onTagsUpdate();
    } catch (error) {
      console.error('Ошибка при удалении тега:', error);
      toast.error(error.response?.data?.error || 'Ошибка при удалении тега');
    }
  };

  const isSuperAdmin = userRank === 'superadmin';
  
  // console.log'PlayerTags debug:', {
  //   userRank,
  //   isSuperAdmin,
  //   userRankFromContext: user?.rank,
  //   playerTags: playerTags.length,
  //   playerTagsType: typeof playerTags,
  //   playerTagsIsArray: Array.isArray(playerTags),
  //   allTags: allTags.length,
  //   allTagsType: typeof allTags,
  //   allTagsIsArray: Array.isArray(allTags)
  // });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-16">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-theme-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-[320px] bg-gmod-lighter/80 rounded-2xl p-6 shadow-lg border border-gmod-lighterer">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xl font-semibold text-theme-primary flex items-center gap-2">
          <Tag size={20} />
          Теги игрока
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => setShowAssignModal(true)}
            className="px-4 py-2 bg-theme-primary text-gmod-white rounded font-semibold text-sm flex items-center gap-1 transition-colors hover:bg-theme-primary-hover"
          >
            <Plus size={14} />
            Назначить тег
          </button>
        )}
      </div>

      {!Array.isArray(playerTags) || playerTags.length === 0 ? (
        <div className="text-center py-8 text-gmod-white opacity-60">
          <Tag size={32} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">У игрока нет назначенных тегов</p>
        </div>
      ) : (
        <div className="space-y-2">
          {Array.isArray(playerTags) && playerTags.map((tag) => (
            <div
              key={tag.id}
              className="flex items-center justify-between p-3 bg-gmod-lighter rounded-lg border border-gmod-lighterer"
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: tag.color }}
                ></div>
                <div>
                  <span className="font-medium text-gmod-white">{tag.name}</span>
                  {tag.description && (
                    <p className="text-xs text-gmod-white opacity-60">{tag.description}</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-xs text-gmod-white opacity-60">
                  {new Date(tag.assignedAt).toLocaleDateString('ru-RU')}
                </span>
                {isSuperAdmin && (
                  <button
                    onClick={() => handleRemoveTag(tag.id)}
                    className="p-1 text-gmod-white opacity-60 hover:text-red-400 hover:bg-gmod-lighterer rounded transition-colors"
                    title="Убрать тег"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Модальное окно для назначения тега */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gmod-dark rounded-lg p-6 w-full max-w-md mx-4 border border-gmod-lighterer">
            <h3 className="text-lg font-semibold text-gmod-white mb-4">Назначить тег игроку</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gmod-white mb-2">
                  Выберите тег
                </label>
                <select
                  value={selectedTagId}
                  onChange={(e) => setSelectedTagId(e.target.value)}
                  className="w-full px-3 py-2 bg-gmod-lighter border border-gmod-lighterer text-gmod-white rounded focus:outline-none focus:ring-2 focus:ring-theme-primary"
                >
                  <option value="">Выберите тег...</option>
                  {Array.isArray(allTags) && allTags
                    .filter(tag => !playerTags.some(pt => pt.id === tag.id))
                    .map((tag) => (
                      <option key={tag.id} value={tag.id}>
                        {tag.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleAssignTag}
                  disabled={!selectedTagId}
                  className="flex-1 bg-theme-primary hover:bg-theme-primary-hover disabled:bg-gmod-lighter text-gmod-white px-4 py-2 rounded font-semibold transition-colors"
                >
                  Назначить
                </button>
                <button
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedTagId('');
                  }}
                  className="flex-1 bg-gmod-lighter hover:bg-gmod-lighterer text-gmod-white px-4 py-2 rounded font-semibold transition-colors"
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для удаления тега */}
      {showRemoveModal && tagToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gmod-dark rounded-lg p-6 w-full max-w-md mx-4 border border-gmod-lighterer">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: tagToRemove.color }}></div>
              <h3 className="text-lg font-semibold text-gmod-white">Убрать тег у игрока</h3>
            </div>
            
            <div className="space-y-4">
              <div className="bg-gmod-lighter rounded-lg p-4 border border-gmod-lighterer">
                <p className="text-gmod-white mb-2">
                  Вы уверены, что хотите убрать тег <span className="font-semibold" style={{ color: tagToRemove.color }}>{tagToRemove.name}</span> у игрока?
                </p>
                {tagToRemove.description && (
                  <p className="text-sm text-gmod-white opacity-60">{tagToRemove.description}</p>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={confirmRemoveTag}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-semibold transition-colors"
                >
                  Убрать тег
                </button>
                <button
                  onClick={() => {
                    setShowRemoveModal(false);
                    setTagToRemove(null);
                  }}
                  className="flex-1 bg-gmod-lighter hover:bg-gmod-lighterer text-gmod-white px-4 py-2 rounded font-semibold transition-colors"
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlayerTags; 